import Chart from 'react-apexcharts';
import { ReactElement } from 'react';
import { bin, HistogramGeneratorNumber } from 'd3-array';

function createBinner(thresholdsFn: () => number[]): HistogramGeneratorNumber<number, number> {
  return bin().domain([-1000, 1000]).thresholds(thresholdsFn());
}

function binPercentages(values: number[], thresholdsFn: () => number[], unit: string): [number[], string[]] {
  if (values.length === 0) {
    return [[], []];
  }
  const binner = createBinner(thresholdsFn);
  const bins = binner(values);
  const total = bins.reduce((sum, b) => sum + b.length, 0);
  const percentages: number[] = bins.map((b) => Math.round((b.length / total) * 100));
  const labels: string[] = bins.map((b) => {
    if ((b.x0 || 0) < -100) {
      return `${b.x1} ${unit} and below`;
    } else if ((b.x1 || 0) > 100) {
      return `${b.x0} ${unit} and above`;
    } else {
      return `${b.x0} to ${b.x1} ${unit}`;
    }
  });
  return [percentages, labels];
}

const percentageFormatter = function (val: any): string {
  return val ? val + '%' : '';
};

interface Series {
  fieldName: string;
  displayName: string;
}

interface HistogramProps {
  data: any;
  title: string;
  series: Series[];
  unit: string;
  opposite?: boolean;
  height?: number;
  colors?: string[];
  thresholds: () => number[];
}

function Histogram(props: HistogramProps): ReactElement {
  const { data, title, series, unit, opposite = false, height = 400, colors, thresholds } = props;

  // Process each series to get percentages and labels
  const processedSeries = series.map((s) => {
    const values: number[] = data[s.fieldName] || [];
    const [percentages, labels] = binPercentages(values, thresholds, unit);
    return {
      name: s.displayName,
      data: percentages,
      labels: labels,
    };
  });

  // Use labels from the first series (they should all be the same)
  const labels = processedSeries[0]?.labels || [];

  const options = {
    noData: { text: 'There is no data to display' },
    chart: {
      id: 'basic-bar',
    },
    xaxis: {
      categories: labels,
      position: 'bottom',
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
    },
    yaxis: {
      opposite: opposite,
      min: 0,
      max: 100,
    },
    tooltip: {
      enabled: true,
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        dataLabels: {
          position: 'top', // top, center, bottom
        },
      },
    },
    colors: colors,
    dataLabels: {
      enabled: true,
      formatter: percentageFormatter,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    title: {
      text: title,
      floating: false,
      offsetY: 0,
      style: {
        align: 'center',
        color: '#444',
        fontSize: '20px',
      },
    },
  };

  const chartSeries = processedSeries.map((s) => ({
    name: s.name,
    data: s.data,
  }));

  return <Chart options={options} series={chartSeries} type="bar" width="100%" height={height} />;
}

export default Histogram;
